import React, { useCallback, useEffect, useState } from 'react';
import { useTranslations } from '../../localization/useTranslations';
import { removeBookmarkedArticle, addBookmarkedArticle } from '../../articles/services/ArticleService';
import CustomSnackbar from '../../components/CustomSnackbar';
import Dialog from '../../common/components/Dialog';
import ArticleDialogContent from '../../articles/components/ArticleDialogContent';
import { isUserLoggedIn } from '../../common/Auth';
import { handleUserActionWithLoginCheck } from '../../common/LoggedOutUserActions';
import useScrollbackAfterRedirect from '../../hooks/useScrollbackAfterRedirect';

interface ArticleMetaProps {
  removeArticle?: (id: string | undefined) => void; // used for MFA - saved articles tab
  savedAt?: string;
  articleAuthors?: any[]; // Array containing author information
  articleData?: any; // Data object for the article
  updateArticles?: () => void;
}

// Displays article meta
const ArticleMeta: React.FC<ArticleMetaProps> = ({
  removeArticle, // used for MFA - saved articles tab
  articleAuthors = [],
  articleData = {},
  updateArticles,
}) => {
  useScrollbackAfterRedirect(); // Restore scroll position if redirected from login
  const translations = useTranslations();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  const articleID = articleData.id;
  const bookmarksData = window?.crm_user?.user_meta?.posts_bookmarks || {};
  const bookmarkedIds = Object.keys(bookmarksData); // Get array of bookmarked article IDs

  // useEffect hook to check bookmark status when component mounts
  useEffect(() => {
    const checkBookmarkStatus = async () => {
      try {
        const isArticleBookmarked = articleID ? bookmarkedIds.includes(articleID.toString()) : false; // Check if article is bookmarked

        setIsBookmarked(isArticleBookmarked); // Set bookmark status
      } catch (error) {
        console.error('Error checking bookmark status:', error);
      }
    };

    checkBookmarkStatus(); // Invoke function to check bookmark status
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Callback function to open dialog
  const openDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  // Callback function to close dialog
  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  // Function to handle bookmark button click
  const handleBookmarkButtonClick = async () => {
    // Open login modal if user is not logged in
    if (!isUserLoggedIn()) {
      if (!handleUserActionWithLoginCheck('bookmark', articleID)) {
        return;
      }
    }

    try {
      // Toggle bookmark status based on whether the article ID is in bookmarkedIds
      if (bookmarkedIds.includes(articleID.toString())) {
        await removeBookmarkedArticle(articleID.toString()); // Remove bookmarked article
        setIsBookmarked(false); // Update bookmark status
        setSnackbarMessage(translations?.snackBarMessages?.articleRemoved || 'Bookmark removed');

        // to handle proper update of mfa / bookmarked articles
        if (removeArticle) {
          removeArticle(articleID.toString());
        }
      } else {
        await addBookmarkedArticle(articleID.toString()); // Add bookmarked article
        setIsBookmarked(true); // Update bookmark status
        setSnackbarMessage(translations?.snackBarMessages?.articleSaved || 'Bookmark added');
      }
      setSnackbarOpen(true);

      // Dispatch bookmarkButtonClick event
      const event = new CustomEvent('bookmarkButtonClick', {
        detail: { articleID }, // Pass article ID in event detail
      });
      window.dispatchEvent(event); // Dispatch event
    } catch (error) {
      console.error('Error handling bookmark:', error);
      setSnackbarMessage(translations?.snackBarMessages?.somethingWentWrong || 'Something went wrong');
      setSnackbarOpen(true);
    }
  };

  // useEffect hook to handle bookmark button click event
  useEffect(() => {
    const handleBookmarkEvent = (event: CustomEvent) => {
      const { detail } = event;
      if (detail && detail.articleID === articleID) {
        setIsBookmarked(!isBookmarked); // Toggle bookmark status
        setSnackbarMessage(
          isBookmarked
            ? translations?.snackBarMessages?.articleRemoved || 'Bookmark removed'
            : translations?.snackBarMessages?.articleSaved || 'Bookmark added'
        );
        setSnackbarOpen(true);
      }
    };

    window.addEventListener('bookmarkButtonClick', handleBookmarkEvent as EventListener); // Add event listener for bookmarkButtonClick

    return () => {
      window.removeEventListener('bookmarkButtonClick', handleBookmarkEvent as EventListener); // Remove event listener on component unmount
    };
  }, [articleID, isBookmarked, translations, updateArticles]);

  // get snackbar message to render and restore previous scrollposition on component load if there was a pre-login bookmark action
  useEffect(() => {
    // Retrieve the snackbar message type from session storage
    const snackbarMessageType = localStorage.getItem('snackbarMessage');

    // Function to get the message based on the message type stored before login
    const getMessage = () => {
      switch (snackbarMessageType) {
        case 'bookmark-success':
          return translations?.snackBarMessages?.articleSaved || 'Article bookmarked successfully!';
        case 'bookmark-error':
          return translations?.snackBarMessages?.failedSavedArticle || 'Error bookmarking article. Please try again.';
        default:
          return '';
      }
    };

    // If a snackbar message is found, display it and remove it from local storage
    if (snackbarMessageType && snackbarMessageType.includes('bookmark')) {
      setSnackbarMessage(getMessage());
      setSnackbarOpen(true);

      localStorage.removeItem('snackbarMessage');
    }
  }, [translations]);

  return (
    <>
      {/* Snackbar component for displaying feedback messages */}
      <CustomSnackbar message={snackbarMessage} open={snackbarOpen} onClose={() => setSnackbarOpen(false)} />
      {/* Dialog component for displaying article content */}
      <Dialog isOpen={isDialogOpen} onClose={closeDialog}>
        <ArticleDialogContent
          title={articleData.title}
          link={articleData.url}
          imageLink={articleData.image}
          onClose={closeDialog}
        />
      </Dialog>

      {/* Article meta section */}
      <div className="article-meta" data-testid="article-meta">
        <div className="article-meta__inner">
          {/* Display author information */}
          <>
            {articleAuthors && articleAuthors.length > 0 && (
              <a
                href={articleAuthors[0]?.url || articleAuthors[0]?.link || articleAuthors[0]?.archive_link}
                className="article-meta__author"
              >
                {articleAuthors[0]?.name}
              </a>
            )}
            {/* Display publish date */}
            <span className="article-meta__publish-date">{articleData.publishDate}</span>
            {/* Display reading time */}

            {articleData.readTime && <span className="article-meta__reading-time">{articleData.readTime}</span>}
          </>
        </div>

        {/* Buttons section */}
        <div className="article-meta__buttons">
          {/* Button to open dialog for sharing */}
          <button
            onClick={openDialog}
            className="article-meta__button article-meta__button--share"
            aria-label={translations?.articleCard?.share || 'Share'}
          >
            <span>{translations?.articleCard?.share || 'Share'}</span>
          </button>

          {/* Button to toggle bookmark status */}
          {window.FEATURE_TOGGLE_MY_ACCOUNT && (
            <button
              onClick={handleBookmarkButtonClick}
              className={`article-meta__button article-meta__button--bookmark ${isBookmarked ? 'active' : ''}`}
              data-id={articleID}
              aria-label={
                isBookmarked ? translations?.articleCard?.saved || 'Saved' : translations?.articleCard?.save || 'Save'
              }
            >
              <span>
                {isBookmarked ? translations?.articleCard?.saved || 'Saved' : translations?.articleCard?.save || 'Save'}
              </span>
            </button>
          )}

          {/* TODO: Premium button with open modal function */}
          {/* {articleData.isPremium && (
            <button
              onClick={openPremiumModal}
              className="article-meta__button article-meta__button--premium"
            >
              <span>{translations?.articleMeta?.unlock || "Unlock"}</span>
            </button>
          )} */}
        </div>
      </div>
    </>
  );
};

export default ArticleMeta;
